import React, { createContext, useState, useContext, useEffect } from 'react';
import { Howl } from 'howler';

const PlayerContext = createContext();

export const PlayerProvider = ({ children }) => {
  const [currentSong, setCurrentSong] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('off'); // 'off', 'all', 'one'
  const [sound, setSound] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const progressInterval = React.useRef(null);

  // Initialize audio player
  useEffect(() => {
    if (currentSong) {
      // Clean up previous sound
      if (sound) {
        sound.unload();
      }

      // Create new Howl instance
      const newSound = new Howl({
        src: [currentSong.filePath ? `https://31.97.105.115.nip.io/api/songs/stream/${currentSong._id}` : currentSong.youtubeUrl],
        html5: true,
        volume: volume,
        format: ['mp3'],
        onend: () => {
          if (repeat === 'one') {
            newSound.play();
          } else if (repeat === 'all' || playlist.length > 1) {
            playNextSong();
          } else {
            setIsPlaying(false);
          }
        },
        onplay: () => {
          console.log('Audio started playing:', currentSong.title);
          setIsPlaying(true);
        },
        onpause: () => {
          setIsPlaying(false);
        },
        onstop: () => {
          setIsPlaying(false);
        },
        onloaderror: (id, err) => {
          console.error('Error loading audio:', currentSong.title, err);
          console.log('Audio source attempted:', currentSong.filePath ? `https://31.97.105.115.nip.io/api/songs/stream/${currentSong._id}` : currentSong.youtubeUrl);
        },
        onplayerror: (id, err) => {
          console.error('Error playing audio:', currentSong.title, err);
          // Essayer de reprendre la lecture après une erreur
          newSound.once('unlock', () => {
            newSound.play();
          });
        }
      });

      // Définir la durée une fois que le son est chargé
      newSound.on('load', () => {
        const songDuration = newSound.duration();
        console.log('Audio loaded, duration:', songDuration);
        setDuration(songDuration);
      });

      setSound(newSound);

      // Auto-play if needed
      if (isPlaying) {
        newSound.play();
      }

      // Nettoyer l'intervalle précédent si existant
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }

      // Configurer un nouvel intervalle pour suivre la progression
      progressInterval.current = setInterval(() => {
        if (newSound && newSound.playing()) {
          const currentTime = newSound.seek();
          setProgress(currentTime);
        }
      }, 1000);

      return () => {
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
        }
        newSound.unload();
      };
    }
  }, [currentSong]);

  // Update volume
  useEffect(() => {
    if (sound) {
      sound.volume(volume);
    }
  }, [volume, sound]);

  // Play a song
  const playSong = (song, songsList = []) => {
    setCurrentSong(song);
    
    if (songsList && songsList.length > 0) {
      setPlaylist(songsList);
      const index = songsList.findIndex(s => s._id === song._id);
      setCurrentIndex(index !== -1 ? index : 0);
    }
  };
  
  // Play a playlist from the beginning
  const playPlaylist = (playlist) => {
    if (playlist && playlist.songs && playlist.songs.length > 0) {
      // Si la playlist contient directement les objets de chansons
      setPlaylist(playlist.songs);
      setCurrentIndex(0);
      setCurrentSong(playlist.songs[0]);
    } else if (playlist && Array.isArray(playlist) && playlist.length > 0) {
      // Si la playlist est un tableau de chansons
      setPlaylist(playlist);
      setCurrentIndex(0);
      setCurrentSong(playlist[0]);
    }
    setIsPlaying(true);
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (!currentSong) return;
    
    if (sound) {
      if (isPlaying) {
        sound.pause();
      } else {
        sound.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Play next song
  const playNextSong = () => {
    if (!playlist || playlist.length <= 1) return;

    let nextIndex;
    if (shuffle) {
      // Get random index different from current
      do {
        nextIndex = Math.floor(Math.random() * playlist.length);
      } while (nextIndex === currentIndex && playlist.length > 1);
    } else {
      // Normal sequential order
      nextIndex = (currentIndex + 1) % playlist.length;
    }

    setCurrentIndex(nextIndex);
    setCurrentSong(playlist[nextIndex]);
  };

  // Play previous song
  const playPrevSong = () => {
    if (!playlist || playlist.length <= 1) return;

    let prevIndex;
    if (shuffle) {
      // Get random index different from current
      do {
        prevIndex = Math.floor(Math.random() * playlist.length);
      } while (prevIndex === currentIndex && playlist.length > 1);
    } else {
      // Normal sequential order
      prevIndex = (currentIndex - 1 + playlist.length) % playlist.length;
    }

    setCurrentIndex(prevIndex);
    setCurrentSong(playlist[prevIndex]);
  };

  // Set volume (0-1)
  const changeVolume = (newVolume) => {
    setVolume(newVolume);
  };

  // Toggle shuffle mode
  const toggleShuffle = () => {
    setShuffle(prev => !prev);
  };

  // Toggle repeat mode (off -> all -> one -> off)
  const toggleRepeat = () => {
    setRepeat(current => {
      if (current === 'off') return 'all';
      if (current === 'all') return 'one';
      return 'off';
    });
  };

  // Seek to position
  const seekTo = (position) => {
    if (sound) {
      sound.seek(position);
    }
  };

  return (
    <PlayerContext.Provider
      value={{
        currentSong,
        playlist,
        currentIndex,
        isPlaying,
        volume,
        shuffle,
        repeat,
        progress,
        duration,
        sound,
        playSong,
        playPlaylist,
        togglePlay,
        playNextSong,
        playPreviousSong: playPrevSong,
        changeVolume,
        toggleShuffle,
        toggleRepeat,
        seekTo
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
